/**
 * MobileBottomBarActionsContext (FT-358)
 *
 * Shell-level context that lets any mounted view register a list of "middle
 * slot" action buttons for the mobile bottom bar. The `MobileBottomBar` reads
 * the currently-active list (the TOP of the action stack) and renders each
 * entry as an icon-tab. When the stack is empty (Dashboard / top-level
 * modules that didn't register actions), the middle slot renders nothing.
 *
 * Design intent (mirrors NavigationBackContext — the FT-356 precedent):
 *   - Views do NOT own the bar. The MobileBottomBar is the single rendering
 *     surface for module actions on mobile.
 *   - Views register their action list on mount via
 *     `useSetBottomBarActions([...])`. Registration is stack-based so nested
 *     drill-downs naturally "pop" back to the parent's actions when they
 *     unmount.
 *   - The hook returns nothing; callers pass their action array and forget.
 *
 * Action shape:
 *   { icon: LucideIcon, label: string, onClick: () => void,
 *     primary?: boolean, disabled?: boolean, key?: string }
 *
 * Stack semantics:
 *   - Each mounted view owns one stack entry (identified by a stable id).
 *   - The entry's actions are updated in place whenever the caller's
 *     `actions` prop shape changes — no push/pop churn on identity changes
 *     from inline array literals.
 *   - MobileBottomBar renders the topmost non-empty entry's actions.
 *   - On unmount, the entry is removed and the previous topmost entry (if
 *     any) becomes active again automatically.
 *
 * Null-context guard: if a caller mounts outside the provider (e.g. during
 * an isolated test render), the hook is a silent no-op. Same forgiveness
 * as NavigationBackContext.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NOOP = () => {};

export const MobileBottomBarActionsContext = createContext({
  actions: [],
  pushActionsEntry: () => 0,
  updateActionsEntry: NOOP,
  removeActionsEntry: NOOP,
});

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return idCounter;
};

/**
 * Provider — mount at the shell level, INSIDE `NavigationBackProvider` but
 * ABOVE `SwipeBackShell` / `TailwindShellInner`, so every view mounted below
 * can register a set of bottom-bar actions.
 */
export function MobileBottomBarActionsProvider({ children }) {
  // Stack entries: { id, actions }. Topmost entry (last element) with a
  // non-empty actions array is active.
  const [stack, setStack] = useState([]);

  const pushActionsEntry = useCallback((actions) => {
    const id = nextId();
    setStack((prev) => [
      ...prev,
      { id, actions: Array.isArray(actions) ? actions : [] },
    ]);
    return id;
  }, []);

  const updateActionsEntry = useCallback((id, actions) => {
    setStack((prev) => {
      const idx = prev.findIndex((entry) => entry.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = {
        id,
        actions: Array.isArray(actions) ? actions : [],
      };
      return next;
    });
  }, []);

  const removeActionsEntry = useCallback((id) => {
    setStack((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // Active list = topmost entry with a non-empty actions array. If the
  // topmost entry is empty (view opted out), fall back to the next entry
  // below — keeps the bar useful when a nested detail view has no actions
  // of its own but its parent does.
  const actions = useMemo(() => {
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].actions && stack[i].actions.length > 0) {
        return stack[i].actions;
      }
    }
    return [];
  }, [stack]);

  const value = useMemo(
    () => ({
      actions,
      pushActionsEntry,
      updateActionsEntry,
      removeActionsEntry,
    }),
    [actions, pushActionsEntry, updateActionsEntry, removeActionsEntry],
  );

  return (
    <MobileBottomBarActionsContext.Provider value={value}>
      {children}
    </MobileBottomBarActionsContext.Provider>
  );
}

/**
 * Stable shape-key for an actions array. We only re-run the update effect
 * when the array's structural shape changes (label / primary / disabled),
 * not when inline arrow-function `onClick` handlers get new identities on
 * every render.
 */
const shapeKey = (actions) => {
  if (!Array.isArray(actions)) return '';
  return actions
    .map(
      (a) =>
        `${a?.key || a?.label || ''}|${a?.primary ? 1 : 0}|${a?.disabled ? 1 : 0}`,
    )
    .join('~');
};

/**
 * FT-404 helpers — stable per-slot onClick wrappers.
 *
 * Stable slot identity for an action at a given index: prefer the explicit
 * `key`, fall back to `label`, then index. The `seen` set disambiguates
 * colliding keys so two slots never share a wrapper.
 */
const slotKeyOf = (a, idx, seen) => {
  let slotKey = a.key || a.label || `idx-${idx}`;
  while (seen.has(slotKey)) slotKey = `${slotKey}~${idx}`;
  seen.add(slotKey);
  return slotKey;
};

/**
 * Refresh the handler map in place from the live actions so each stable
 * wrapper always delegates to the freshest inline closure — even when the
 * actions SHAPE is unchanged (a caller whose onClick captures state that
 * mutates on a sync tick without touching label/disabled). Only the closures
 * change; wrapper identities stay put.
 */
const refreshHandlers = (handlers, actions) => {
  const seen = new Set();
  (Array.isArray(actions) ? actions : []).forEach((a, idx) => {
    if (!a) return;
    handlers.set(slotKeyOf(a, idx, seen), a.onClick);
  });
};

/**
 * Wrap an actions array: replace each inline onClick with its stable wrapper
 * (created on first sight, delegating to the latest closure via `handlers`)
 * and prune wrappers for slots that no longer exist. Used when pushing/
 * updating the shell stack — the <button onClick> identity never changes.
 */
const buildStableActions = (raw, wrappers, handlers) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const keptKeys = new Set();
  const out = raw.map((a, idx) => {
    if (!a) return a;
    const slotKey = slotKeyOf(a, idx, seen);
    keptKeys.add(slotKey);
    if (!wrappers.has(slotKey)) {
      wrappers.set(slotKey, (...args) => handlers.get(slotKey)?.(...args));
    }
    return { ...a, onClick: wrappers.get(slotKey) };
  });
  Array.from(wrappers.keys()).forEach((existing) => {
    if (!keptKeys.has(existing)) wrappers.delete(existing);
  });
  return out;
};

/**
 * useSetBottomBarActions(actions)
 *
 * Register a list of middle-slot actions for this view. On mount the list is
 * pushed onto the shell-level stack; on unmount it is popped. When the
 * caller's `actions` shape (labels / primary / disabled flags) changes, the
 * existing stack entry is updated in place — no push/pop churn from inline
 * array literals whose only diff is a new `onClick` closure.
 *
 * Pass an empty array (or `null`) to indicate "no middle actions for me".
 * The stack entry still exists (so cleanup on unmount works cleanly) but is
 * skipped by the bar; whichever parent last registered a real list remains
 * active.
 *
 * Safe outside the provider (silent no-op).
 */
export function useSetBottomBarActions(actions) {
  const ctx = useContext(MobileBottomBarActionsContext);
  const { pushActionsEntry, updateActionsEntry, removeActionsEntry } =
    ctx || {};

  // Keep the latest actions in a ref so the mount effect always pushes the
  // freshest closures (not a stale snapshot from initial render).
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // ── FT-404: stable per-slot onClick wrappers ────────────────────────────
  // The bar's <button onClick> comes straight from the action object we push
  // onto the stack. Callers pass INLINE arrow `onClick` closures (per the
  // FT-358 "pass and forget" design), so a fresh closure is minted every
  // render. Storing those raw closures meant any legitimate SHAPE change
  // (e.g. "Sign Off" → "Signing…", a `disabled` flip, a tab switch) re-rendered
  // MobileBottomBar with a brand-new `onClick` identity on the live <button> —
  // and iOS drops an in-flight touch when the handler swaps mid-render, so the
  // first tap silently failed and only the second registered.
  //
  // Fix mirrors the FT-401 NavigationBackContext `stableWrapper` pattern:
  //   handlersRef — slotKey → the LATEST raw onClick (refreshed in place every
  //     render, never replaced).
  //   wrappersRef — slotKey → ONE stable wrapper whose identity never changes;
  //     it reads handlersRef at call time, so it always fires the freshest
  //     closure without ever swapping the <button onClick> reference.
  const handlersRef = useRef(new Map());
  const wrappersRef = useRef(new Map());

  // Every render: point the wrappers at the freshest closures (no re-render).
  refreshHandlers(handlersRef.current, actions);

  const wrap = useCallback(
    (raw) => buildStableActions(raw, wrappersRef.current, handlersRef.current),
    [],
  );

  // Track the id of our stack entry across renders.
  const idRef = useRef(null);

  // Push on mount, pop on unmount. Silent no-op if we're outside a provider.
  useEffect(() => {
    if (!pushActionsEntry || !removeActionsEntry) return undefined;
    const id = pushActionsEntry(wrap(actionsRef.current || []));
    idRef.current = id;
    return () => {
      removeActionsEntry(id);
      idRef.current = null;
    };
    // We deliberately push exactly once per view lifecycle. Shape updates
    // are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update in place when the actions SHAPE changes (post-mount only — the push
  // above uses the initial value). Ignores pure identity churn from inline
  // arrow functions. The onClick wrappers stay stable across the update
  // (FT-404) so the live <button> handler never swaps mid-tap.
  const firstRun = useRef(true);
  const currentShape = shapeKey(actions);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (idRef.current != null && updateActionsEntry) {
      updateActionsEntry(idRef.current, wrap(actionsRef.current || []));
    }
  }, [currentShape, updateActionsEntry, wrap]);
}

/**
 * useCurrentBottomBarActions() — read-only accessor. Returns the currently-
 * active actions list (the topmost non-empty entry on the stack) or an empty
 * array. Used by MobileBottomBar to render the middle slot.
 */
export function useCurrentBottomBarActions() {
  const ctx = useContext(MobileBottomBarActionsContext);
  return ctx?.actions || [];
}
